import plan from './claverblatPlan.json';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import { convexHull, obbCorners } from '../../../geo/polygon';
import { obbPolygonOverlap } from '../../../geo/sat';
import { intersection } from '../../../geo/clip';
import { polygonArea } from '../../../geo/polygon';
import type { Vec2 } from '../../../geo/types';
import { lotFrame } from '../lotFrame';
import { footprintInside, lotsOverlap } from '../lotFootprint';
import { obbFromFrontEdge } from '../lotObb';
import type { Lot, ReservedFootprint } from '../types';

export const CLAVERBLAT = plan;
export const CLAVERBLAT_SPIJKER = 'claverblat-estimated/spijker';

/** A rejected property fit must retain the ordinary recorded house, including its renderer. */
export function isClaverblatQuay(lot: Lot): boolean {
  return (
    lot.reconstruction?.id === plan.quayReading &&
    lot.depth === plan.quay.depth &&
    lot.reconstruction.params.storeys === plan.quay.floors.length
  );
}

function body(lot: Lot, width: number, depth: number, front?: Vec2): Lot {
  const frame = lotFrame(lot);
  const at = front ?? frame.frontMid;
  const point = (sign: number): Vec2 => [
    at[0] + (frame.right[0] * width * sign) / 2,
    at[1] + (frame.right[1] * width * sign) / 2,
  ];
  const obb = obbFromFrontEdge(point(-1), point(1), depth, frame.facing);
  return { ...lot, obb, corners: obbCorners(obb), frontage: width, depth };
}

function endEdge(lot: Lot, depth: number): readonly Vec2[] {
  const frame = lotFrame(lot);
  return [-1, 1].map((sign): Vec2 => [
    frame.frontMid[0] - frame.facing[0] * depth + (frame.right[0] * lot.frontage * sign) / 2,
    frame.frontMid[1] - frame.facing[1] * depth + (frame.right[1] * lot.frontage * sign) / 2,
  ]);
}

/** Both private courts belong to the connected property, not to inferred neighbours. */
function courtyards(house: Lot, store: Lot, home: Lot) {
  return [
    convexHull([...endEdge(house, house.depth), ...endEdge(store, 0)]),
    convexHull([...endEdge(store, store.depth), ...endEdge(home, home.depth)]),
  ];
}

export function claverblatYardReservations(lots: readonly Lot[]): readonly ReservedFootprint[] {
  const house = lots.find(isClaverblatQuay);
  const store = lots.find((l) => l.reconstruction?.id === CLAVERBLAT_SPIJKER);
  const home = lots.find((l) => l.reconstruction?.id === plan.dikeReading);
  return house && store && home
    ? courtyards(house, store, home).map((outer, i) => ({
        id: `claverblat-estimated/courtyard-${i + 1}`,
        polygon: { outer, holes: [] },
      }))
    : [];
}

/** A recorded property precedes inferred rear boundaries; street front lines remain fixed. */
export function fitClaverblatPlots(
  lots: readonly Lot[],
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
): readonly Lot[] {
  const quay = lots.find((l) => l.reconstruction?.id === plan.quayReading);
  const dike = lots.find((l) => l.reconstruction?.id === plan.dikeReading);
  const access = lots.find((l) => l.reconstruction?.id === plan.accessReading);
  if (
    !quay ||
    !dike ||
    !access ||
    quay.plot ||
    dike.plot ||
    access.plot ||
    access.frontage <= plan.alleyWidth
  )
    return lots;
  const frame = lotFrame(quay);
  const house = body(quay, Math.min(quay.frontage, plan.quay.width), plan.quay.depth);
  const home = body(dike, Math.min(dike.frontage, plan.dike.width), plan.dike.depth);
  const front: Vec2 = [
    frame.frontMid[0] - frame.facing[0] * (plan.quay.depth + plan.yard),
    frame.frontMid[1] - frame.facing[1] * (plan.quay.depth + plan.yard),
  ];
  const store: Lot = {
    ...body(quay, plan.spijker.width, plan.spijker.depth, front),
    sourceId: 'claverblat-estimated/yard',
    ordinal: 10_900,
    kind: 'backhouse',
    reconstruction: {
      id: CLAVERBLAT_SPIJKER,
      row: 'claverblat-estimated',
      evidence: 'inferred',
      params: {
        storeys: plan.spijker.storeys,
        storeyHeight: plan.spijker.storeyHeight,
        roofMaterial: 'tile',
        frontType: 'brick',
        frontGable: 'plain',
        backGable: 'plain',
      },
    },
  };
  const accessFrame = lotFrame(access);
  // Keep this neighbour's edge beside the property; take the passage from its southern edge.
  const narrowed = body(access, access.frontage - plan.alleyWidth, access.depth, [
    accessFrame.frontMid[0] + (accessFrame.right[0] * plan.alleyWidth) / 2,
    accessFrame.frontMid[1] + (accessFrame.right[1] * plan.alleyWidth) / 2,
  ]);
  const changed = [house, home, store, narrowed];
  const yards = courtyards(house, store, home);
  const obstructsProperty = (lot: Lot) =>
    changed.some((n) => lotsOverlap(lot, n, 0.001)) ||
    yards.some((ring) => obbPolygonOverlap(lot.obb, ring, 0.001));
  if (
    changed.some(
      (l, i) =>
        !footprintInside(buildable, l.corners) ||
        intersection(
          [{ outer: [...l.corners], holes: [] }],
          reserved.map((r) => r.polygon)
        ).reduce((sum, p) => sum + polygonArea(p), 0) > 0.001 ||
        changed.slice(0, i).some((n) => lotsOverlap(l, n, 0.001))
    )
  )
    return lots;
  const fitted = new Map<Lot, Lot>([
    [quay, house],
    [dike, home],
    [access, narrowed],
  ]);
  for (const lot of lots) {
    if (fitted.has(lot)) continue;
    if (!obstructsProperty(lot)) continue;
    // A painted body or oblique plot cannot be trimmed to justify this estimated association.
    if (lot.plot || lot.reconstruction?.evidence !== 'inferred') return lots;
    let depth = lot.depth;
    let shortened = lot;
    while (depth >= 6 && obstructsProperty(shortened)) {
      depth -= 0.25;
      shortened = body(lot, lot.frontage, depth);
    }
    if (depth < 6) return lots;
    fitted.set(lot, shortened);
  }
  const eaves = plan.quay.floors.reduce((sum, h) => sum + h, 0);
  fitted.set(quay, {
    ...house,
    reconstruction: {
      ...house.reconstruction!,
      params: {
        ...house.reconstruction!.params,
        storeys: plan.quay.floors.length,
        storeyHeight: eaves / plan.quay.floors.length,
        pitch: Math.atan((2 * plan.quay.roofRise) / house.frontage),
        frontType: 'brick',
        frontGable: 'plain',
        backGable: 'plain',
      },
    },
  });
  fitted.set(dike, {
    ...home,
    reconstruction: {
      ...home.reconstruction!,
      params: {
        ...home.reconstruction!.params,
        storeys: plan.dike.storeys,
        storeyHeight: plan.dike.storeyHeight,
      },
    },
  });
  return [...lots.map((l) => fitted.get(l) ?? l), store].map((l, index) => ({ ...l, index }));
}
